import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../../lib/api';
import { CLIENT_APPROVABLE_TYPES, clientApprovalTypeLabel } from '../../../lib/clientApprovals';
import { type ClientApprovalSettings, FORWARD_ONLY_COPY } from '../../../lib/clientApprovalPortal';
import { CONFIRM_APPROVER_COPY } from '../../settings/StakeholdersTab';

/**
 * #3187 APPROVE.4 — the Lead Approver panel and the MANAGED contact's
 * confirm-approver UI (design v1.1 §5.4; #3185 D11).
 *
 * Reads GET /client/projects/:id/approval-settings (G1, ruling 6564562912);
 * writes with 2b's routes, unchanged (clientApprovalLead.routes.ts):
 *   PUT  /client/projects/:id/approval-policy        { mode }        Lead only
 *   PUT  /client/projects/:id/approval-assignments   { assignments } Lead only
 *   POST /client/projects/:id/stakeholders/:sid/confirm-approver     confirmer
 * The panel follows the server's verdicts (viewerIsLead, viewerCanConfirm) and
 * the server re-checks every write. Any read failure (404 dark or unrelated,
 * 403 neither Lead nor confirmer) renders nothing.
 */
export default function ClientApprovalLeadPanel({ projectId }: { projectId: string }) {
  const [settings, setSettings] = useState<ClientApprovalSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string[]>>({});

  // The first read decides whether the panel shows at all (any failure: no
  // panel). A re-read after a write keeps the panel and the viewer's unsaved
  // picker edits: only the type just saved takes the server's value (R1 P3-6).
  const load = useCallback(async (after?: { savedType?: string }) => {
    try {
      const s = await apiFetch<ClientApprovalSettings>(`/client/projects/${encodeURIComponent(projectId)}/approval-settings`);
      setSettings(s);
      if (!after) setDraft(s.assignments ?? {});
      else if (after.savedType) {
        const t = after.savedType;
        setDraft((d) => ({ ...d, [t]: s.assignments?.[t] ?? [] }));
      }
    } catch {
      if (!after) { setSettings(null); return; }
      setError('Saved, but the approver list could not be refreshed. Reload the page to see the latest.');
    }
  }, [projectId]);

  useEffect(() => { void load(); }, [load]);

  if (!settings || (!settings.viewerIsLead && !settings.viewerCanConfirm)) return null;

  const run = async (fn: () => Promise<unknown>, savedType?: string) => {
    setSaving(true);
    setError(null);
    try {
      await fn();
      await load({ savedType });
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const setMode = (mode: 'ALL' | 'ASSIGNED') => run(() =>
    apiFetch(`/client/projects/${encodeURIComponent(projectId)}/approval-policy`, { method: 'PUT', body: JSON.stringify({ mode }) }));
  // Codex R2-2: an assignee who is no longer eligible (revoked, no longer an
  // Approver) is shown on its deliverable but never resubmitted, so a save
  // removes them; resubmitting would have made 2b's writer refuse every save.
  const ineligible = new Map((settings.ineligibleAssignees ?? []).map((a) => [a.stakeholderId, a.name]));
  const saveType = (type: string) => run(() =>
    apiFetch(`/client/projects/${encodeURIComponent(projectId)}/approval-assignments`, {
      method: 'PUT', body: JSON.stringify({ assignments: { [type]: (draft[type] ?? []).filter((id) => !ineligible.has(id)) } }),
    }), type);
  const confirm = (stakeholderId: string) => run(() =>
    apiFetch(`/client/projects/${encodeURIComponent(projectId)}/stakeholders/${encodeURIComponent(stakeholderId)}/confirm-approver`, { method: 'POST' }));

  // Confirmed approvers can be assigned. One already assigned who is now
  // awaiting confirmation stays listed (marked) so it can be unassigned (R1 P3-6).
  const assignableFor = (type: string) =>
    settings.approvers.filter((a) => !ineligible.has(a.stakeholderId)
      && (!a.confirmationPending || (draft[type] ?? []).includes(a.stakeholderId)));
  const ineligibleFor = (type: string) => (draft[type] ?? []).filter((id) => ineligible.has(id));
  const awaitingConfirmation = settings.approvers.filter((a) => a.confirmationPending);
  const toggle = (type: string, id: string) => setDraft((d) => {
    const cur = new Set(d[type] ?? []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    return { ...d, [type]: [...cur] };
  });

  return (
    <section data-testid="client-approval-lead-panel" style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Approvers</h2>
      {error && <div data-testid="lead-panel-error" role="alert" style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {settings.viewerIsLead && (
        <div data-testid="lead-settings" style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Who approves each deliverable</div>
          <div role="radiogroup" aria-label="Approval mode" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            <label style={{ display: 'flex', gap: 8, fontSize: 13 }}>
              <input type="radio" name="approval-mode" data-testid="mode-all" disabled={saving}
                checked={settings.mode === 'ALL'} onChange={() => { void setMode('ALL'); }} />
              All approvers approve every deliverable
            </label>
            <label style={{ display: 'flex', gap: 8, fontSize: 13 }}>
              <input type="radio" name="approval-mode" data-testid="mode-assigned" disabled={saving}
                checked={settings.mode === 'ASSIGNED'} onChange={() => { void setMode('ASSIGNED'); }} />
              Assigned approvers per deliverable
            </label>
          </div>
          <p data-testid="forward-only-note" style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', margin: '0 0 12px' }}>{FORWARD_ONLY_COPY}</p>

          {settings.mode === 'ASSIGNED' && (
            <div data-testid="assignment-pickers">
              {CLIENT_APPROVABLE_TYPES.map((type) => (
                <fieldset key={type} data-testid={`picker-${type}`} style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                  <legend style={{ fontSize: 12, fontWeight: 600 }}>{clientApprovalTypeLabel(type)}</legend>
                  {ineligibleFor(type).map((id) => (
                    <span key={id} data-testid={`ineligible-${type}-${id}`} style={{ display: 'inline-flex', fontSize: 12, marginRight: 12, color: 'var(--text-secondary, #6b7280)' }}>
                      {ineligible.get(id)} (No longer eligible; saving removes them)
                    </span>
                  ))}
                  {assignableFor(type).length === 0 ? (
                    <span style={{ fontSize: 12 }}>No approvers to assign yet.</span>
                  ) : assignableFor(type).map((a) => (
                    <label key={a.stakeholderId} style={{ display: 'inline-flex', gap: 6, fontSize: 12, marginRight: 12 }}>
                      <input type="checkbox" data-testid={`assign-${type}-${a.stakeholderId}`} disabled={saving}
                        checked={(draft[type] ?? []).includes(a.stakeholderId)} onChange={() => toggle(type, a.stakeholderId)} />
                      {a.name}{a.isLead ? ' (Lead)' : ''}{a.confirmationPending ? ' (awaiting confirmation)' : ''}
                    </label>
                  ))}
                  <button type="button" data-testid={`save-${type}`} disabled={saving} onClick={() => { void saveType(type); }}
                    style={{ marginLeft: 8, padding: '2px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--primary)', background: 'transparent', color: 'var(--primary)', cursor: 'pointer' }}>
                    Save
                  </button>
                </fieldset>
              ))}
            </div>
          )}
        </div>
      )}

      {settings.viewerCanConfirm && (
        <div data-testid="confirm-approvers" style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Approvers awaiting your confirmation</div>
          <p style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', margin: '0 0 8px' }}>{CONFIRM_APPROVER_COPY}</p>
          {awaitingConfirmation.length === 0 ? (
            <span data-testid="confirm-approvers-none" style={{ fontSize: 12 }}>No one is waiting for confirmation.</span>
          ) : awaitingConfirmation.map((a) => (
            <div key={a.stakeholderId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 13 }}>
              <span>{a.name}</span>
              <button type="button" data-testid={`confirm-${a.stakeholderId}`} disabled={saving} onClick={() => { void confirm(a.stakeholderId); }}
                style={{ padding: '2px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--primary)', background: 'transparent', color: 'var(--primary)', cursor: 'pointer' }}>
                Confirm
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
